import * as XLSX from 'xlsx';

export const MAX_TICKET_SPREADSHEET_ROWS = 500;
export const MAX_TICKET_SPREADSHEET_COLUMNS = 50;

export interface TicketSpreadsheetSheet {
  readonly name: string;
  readonly rows: readonly (readonly string[])[];
  readonly columns: readonly string[];
  readonly startRow: number;
  readonly totalRows: number;
  readonly totalColumns: number;
  readonly truncated: boolean;
}

export interface TicketSpreadsheetPreviewData {
  readonly sheets: readonly TicketSpreadsheetSheet[];
}

export async function loadTicketSpreadsheetPreview(
  source: Blob | string,
  signal?: AbortSignal,
  fetcher: typeof fetch = fetch,
): Promise<TicketSpreadsheetPreviewData> {
  const blob = typeof source === 'string'
    ? await fetchSpreadsheetBlob(source, signal, fetcher)
    : source;
  const buffer = await blob.arrayBuffer();
  throwIfAborted(signal);

  const workbook = XLSX.read(buffer, {
    type: 'array',
    cellFormula: false,
    cellHTML: false,
    cellNF: false,
    cellStyles: false,
  });
  throwIfAborted(signal);

  return {
    sheets: workbook.SheetNames.map((name) => buildSheet(name, workbook.Sheets[name])),
  };
}

function buildSheet(name: string, worksheet: XLSX.WorkSheet): TicketSpreadsheetSheet {
  const reference = worksheet['!ref'];
  if (!reference) {
    return {
      name,
      rows: [],
      columns: [],
      startRow: 1,
      totalRows: 0,
      totalColumns: 0,
      truncated: false,
    };
  }

  const decoded = XLSX.utils.decode_range(reference);
  const totalRows = decoded.e.r - decoded.s.r + 1;
  const totalColumns = decoded.e.c - decoded.s.c + 1;
  const endRow = Math.min(decoded.e.r, decoded.s.r + MAX_TICKET_SPREADSHEET_ROWS - 1);
  const endColumn = Math.min(decoded.e.c, decoded.s.c + MAX_TICKET_SPREADSHEET_COLUMNS - 1);
  const range = XLSX.utils.encode_range(
    { r: decoded.s.r, c: decoded.s.c },
    { r: endRow, c: endColumn },
  );
  const values = XLSX.utils.sheet_to_json<unknown[]>(worksheet, {
    header: 1,
    raw: false,
    defval: '',
    blankrows: true,
    range,
  });
  const columnCount = endColumn - decoded.s.c + 1;

  return {
    name,
    rows: values.map((row) =>
      Array.from({ length: columnCount }, (_, index) => String(row[index] ?? '')),
    ),
    columns: Array.from(
      { length: columnCount },
      (_, index) => XLSX.utils.encode_col(decoded.s.c + index),
    ),
    startRow: decoded.s.r + 1,
    totalRows,
    totalColumns,
    truncated:
      totalRows > MAX_TICKET_SPREADSHEET_ROWS ||
      totalColumns > MAX_TICKET_SPREADSHEET_COLUMNS,
  };
}

async function fetchSpreadsheetBlob(
  url: string,
  signal: AbortSignal | undefined,
  fetcher: typeof fetch,
): Promise<Blob> {
  const response = await fetcher(url, { signal });
  if (!response.ok) {
    throw new Error(`Unable to load spreadsheet preview (${response.status})`);
  }
  return response.blob();
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException('The operation was aborted', 'AbortError');
}
